// CF-MAINNET-91: Plausible analytics dispatcher.
//
// Single source of truth for all funnel-event emission. Wraps window.plausible
// with type-safe helpers + canonical-host gating + central string sanitization
// + script-load failure detection.
//
// Design constraints (Scorer scope-sign 2026-04-26T05:33Z):
// - No new tsc errors → no `import.meta.env` usage; canonical-host gate instead.
// - No PII / wallet addresses / Telegram IDs / receipts / raw stacks in props.
// - All string props bounded; central sanitizer.
// - Failure observable but not fatal — analytics must not break play.
// - dev/Vercel preview hostnames skip emission entirely (would pollute prod stats).

declare global {
    interface Window {
        plausible?: ((event: string, opts?: { props?: Record<string, string | number | boolean> }) => void) & {
            q?: unknown[];
            init?: (opts?: unknown) => void;
            o?: unknown;
        };
    }
}

const TRACKING_HOST = 'cricketflip.com';
const PROP_LIMIT = 80;

/**
 * Canonical-host gate. Returns true only on the production domain. Vercel
 * preview deploys (e.g. cricketflip-946zn00vf-ro-ket.vercel.app), localhost,
 * and any other host return false → events are silently dropped.
 */
function trackingEnabled(): boolean {
    return typeof window !== 'undefined' && window.location.hostname === TRACKING_HOST;
}

/**
 * Bound a string to PROP_LIMIT (default 80) chars. Used for ALL string props
 * (reason, provider, source, where) so that nothing accidentally leaks a
 * stack trace, address, or receipt JSON into Plausible.
 */
function sanitize(value: string | number | boolean): string | number | boolean {
    if (typeof value !== 'string') return value;
    return value.length > PROP_LIMIT ? value.slice(0, PROP_LIMIT) : value;
}

function sanitizeProps(props?: Record<string, string | number | boolean>): Record<string, string | number | boolean> | undefined {
    if (!props) return undefined;
    const out: Record<string, string | number | boolean> = {};
    for (const [k, v] of Object.entries(props)) {
        out[k] = sanitize(v);
    }
    return out;
}

let loadFailureWarned = false;
let scriptMissingWarned = false;

/**
 * One-shot warning for script-load failure (Plausible CDN unreachable, ad-blocker
 * removed it, etc). Called from track.markPlausibleLoadFailed which is wired in
 * the index.html bootstrap as the script tag's onerror handler.
 */
export function markPlausibleLoadFailed(reason: string): void {
    if (loadFailureWarned) return;
    loadFailureWarned = true;
    console.warn(`[track] Plausible script load FAILED — events will be dropped this session. Reason: ${reason}`);
}

let dispatchThrowWarned = false;

function emit(event: string, props?: Record<string, string | number | boolean>): void {
    if (!trackingEnabled()) return; // dev/preview — no pollution
    if (typeof window === 'undefined') return;
    // CF-MAINNET-91: callable check + try/catch. Even with the queue stub,
    // an ad-blocker or extension can replace window.plausible with a truthy
    // non-function or a throwing shim. We must NEVER let analytics dispatch
    // throw out into wallet-connect / deposit / withdraw / flip flows. Same
    // doctrine as CF-MAINNET-89: failure observable (warn once) but never
    // propagates. Per Scorer code-sig blocker 2026-04-26T05:52Z.
    const fn = window.plausible;
    if (typeof fn !== 'function') {
        if (!scriptMissingWarned) {
            console.warn('[track] window.plausible missing or non-function; bootstrap may have failed or extension replaced it');
            scriptMissingWarned = true;
        }
        return;
    }
    try {
        const safe = sanitizeProps(props);
        fn(event, safe ? { props: safe } : undefined);
    } catch (err) {
        if (!dispatchThrowWarned) {
            // One-shot warn (parallel to scriptMissingWarned). Subsequent events
            // are still dispatched + try/caught; only the noisy warn is silenced.
            console.warn('[track] window.plausible call threw — warning silenced for subsequent throws this session (events still attempted, errors still caught)', err);
            dispatchThrowWarned = true;
        }
    }
}

// ═══════════════════════════════════════════════════════════════════════════
// Public API — one helper per funnel event. Each helper is type-safe at call
// sites + grep-friendly. Add new helpers here, never call window.plausible
// directly from components.
// ═══════════════════════════════════════════════════════════════════════════

export const track = {
    appOpen: (source: 'telegram' | 'direct') => emit('app_open', { source }),

    walletConnectStart: (provider: string) => emit('wallet_connect_start', { provider }),
    walletConnectSuccess: (provider: string) => emit('wallet_connect_success', { provider }),
    walletConnectFail: (provider: string, reason: string) => emit('wallet_connect_fail', { provider, reason }),

    depositInitiated: (amountTon: number) => emit('deposit_initiated', { amount_ton: amountTon }),
    depositSuccess: (amountTon: number, path: 'credited' | 'already_credited_open') =>
        emit('deposit_success', { amount_ton: amountTon, path }),
    depositFail: (amountTon: number, reason: string) => emit('deposit_fail', { amount_ton: amountTon, reason }),

    firstFlip: (choice: 'heads' | 'tails', amountTon: number) => emit('first_flip', { choice, amount_ton: amountTon }),
    flipResolved: (choice: 'heads' | 'tails', outcome: 'heads' | 'tails', amountTon: number, won: boolean) =>
        emit('flip_resolved', { choice, outcome, amount_ton: amountTon, won }),
    practiceFlip: (choice: 'heads' | 'tails', won: boolean) => emit('practice_flip', { choice, won }),

    withdrawInitiated: (amountTon: number) => emit('withdraw_initiated', { amount_ton: amountTon }),
    withdrawSuccess: (amountTon: number) => emit('withdraw_success', { amount_ton: amountTon }),
    withdrawFail: (amountTon: number, reason: string) => emit('withdraw_fail', { amount_ton: amountTon, reason }),

    verifyTabOpened: () => emit('verify_tab_opened'),

    churnSignal: (where: string) => emit('churn_signal', { where }),
};

// ═══════════════════════════════════════════════════════════════════════════
// First-flip detection helper. localStorage with safe try/catch + session-ref
// fallback so a private-mode browser doesn't spam first_flip on every flip.
// Only the REAL flip path (state-channel) calls this — practice flips do NOT.
// ═══════════════════════════════════════════════════════════════════════════

const FIRST_FLIP_KEY = 'cf_first_flip_done';
let sessionFirstFlipDone = false; // session-ref fallback when localStorage unavailable

/**
 * Returns true if this is the user's first REAL flip (not practice). Caller
 * should invoke track.firstFlip(...) when this returns true. Always sets the
 * flag so subsequent calls return false.
 */
export function consumeFirstFlipFlag(): boolean {
    // Try localStorage first (persistent across sessions)
    try {
        if (localStorage.getItem(FIRST_FLIP_KEY) === '1') return false;
        localStorage.setItem(FIRST_FLIP_KEY, '1');
        return true;
    } catch {
        // localStorage unavailable (private mode, quota exceeded, sandboxed iframe).
        // Fall back to session ref so we don't spam first_flip every flip.
        if (sessionFirstFlipDone) return false;
        sessionFirstFlipDone = true;
        return true;
    }
}
